import { Alert, Button, Grid, Group, Paper, Title } from "@mantine/core";
import React from "react";
import { ZodError } from "zod";
import DocxUpload from "../../../../components/DocxReport/NewDocxUpload/DocxUpload";
import { ZodErrorModal } from "../../../../components/ZodErrorModal/ZodErrorModal";
import { setShowFailedMessageThunk } from "../../../../global/globalSlice";
import { useAppDispatch } from "../../../../hooks";
import { IBygningsfysikkPremissRapport } from "../../../../mc-shared/reportSchemas/bygningsfysikk/bygningsfysikkPremissrapport/bygningsfysikkPremissrapportSchema";
import { IElektroSamsvarserklaringEkomnettRapport } from "../../../../mc-shared/reportSchemas/elektroSamsvarserklaringEkomnettSchema";
import { IElektroSamsvarserklaringFELRapport } from "../../../../mc-shared/reportSchemas/elektroSamsvarserklaringFELSchema";
import { IGKRapport } from "../../../../mc-shared/reportSchemas/gkReportSchema";
import { IInternJuridiskUnderkonsulentRapport } from "../../../../mc-shared/reportSchemas/internJuridiskSchema";
import { IReportTemplate, ReportCode } from "../../../../mc-shared/reportSchemas/reportSchemas";
import { ISJArapport } from "../../../../mc-shared/reportSchemas/sjaSchema";
import AkustikkPremissrapport from "../akustikk/AkustikkPremissrapport";
import Brannrapport from "../brannrapport/Brannrapport";
import { IBrandrapportConfig } from "../brannrapport/types";
import BygningsfysikkPremissrapport from "../bygningsfysikk/BygningsfysikkPremissrapport";
import { EksempelRapport } from "../eksempelrapport/EksempelRapport";
import RapportSamsvarserklaringEkomnett from "../elektro/samsvarserklaring/RapportSamsvarserklaringEkomnett";
import RapportSamsvarserklaringFEL from "../elektro/samsvarserklaring/RapportSamsvarserklaringFEL";
import SJARapport from "../elektro/SJA/SJArapport";
import GeoteknikOmraadestabilitetsRapport from "../geoteknik/GeoteknikOmraadestabilitetsRapport";
import Geoteknikrapport from "../geoteknik/Geoteknikrapport";
import GKrapport from "../gkRapport/GKrapport";
import UnderkonsulentRapport from "../internjuridisk/UnderkonsulentRapport";
import { doGenerateReportAppDocx } from "../reportBoardAPI";
import { buildCurrentReportDocxData } from "../reportSelectors";
import { setEditedDataInRedux } from "../reportSlice";
import { doDownloadReportTemplate, doGetReportTemplates, doUploadReportTemplates } from "../reportTemplateAPI";
import { ensureOverleveringChecklistReport } from "../startupChecklist/StartupChecklistReportUtils";
import StartupChecklistReport from "../startupChecklist/StartupOverleveringChecklistReport";

const somedate = new Date("2023-01-01T12:00:00Z");
const ReportAdminPage: React.FC<{ reportId: string }> = ({ reportId }) => {
  const [reportErrorsModalOpen, setReportErrorsModalOpen] = React.useState<boolean>(false);
  const [reportTemplate, setReportTemplate] = React.useState<IReportTemplate>(null);
  const [reportParseErrors, setReportParseErrors] = React.useState<ZodError>(null);
  const [editData, setEditData] = React.useState<object>(null);
  const [dataToGenerateDocxWith, setDataToGenerateDocxWith] = React.useState<object>(null);

  React.useEffect(() => {
    init();
  }, []);

  const dispatch = useAppDispatch();

  const init = async () => {
    const _reportTemplates = await doGetReportTemplates();

    const _reportTemplate = _reportTemplates.find((tmpl) => tmpl.templateId === Number(reportId));

    if (_reportTemplate != null || process.env.NODE_ENV === "development") {
      setReportTemplate(_reportTemplate);
      const normalizedData =
        _reportTemplate.templateId === 901
          ? ensureOverleveringChecklistReport(_reportTemplate.data as any)
          : _reportTemplate.data;
      dispatch(
        setEditedDataInRedux({
          code: Number(reportId) as ReportCode,
          data: normalizedData as any,
        }),
      );
    } else {
      alert("no access");
    }
  };

  const uploadComplete = () => {
    alert("File successfully uploaded");
  };

  const downloadTemplate = async () => {
    try {
      await doDownloadReportTemplate(reportId);
    } catch (error) {
      alert("Feil under nedlasting. Det er sannsynligvis ingen mal lastet opp ennå.");
    }
  };

  const upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await doUploadReportTemplates(reportTemplate.templateId, e);
    if (e.target != null) {
      e.target.value = null;
    }
  };

  const setData = (_editedData: object) => {
    setEditData(_editedData);
    setReportParseErrors(null);

    const docx = buildCurrentReportDocxData({
      code: reportTemplate.templateId as any,
      editedData: { [reportTemplate.templateId]: _editedData } as any,
    });
    setDataToGenerateDocxWith(docx as any);
  };

  const handleError = (errors: ZodError) => {
    setReportParseErrors(errors);
    console.log(errors);
  };

  return (
    <div className="d-flex flex-column overflow-auto h-100">
      {reportTemplate != null && (
        <>
          <Paper bg="dark.8" px="md" py="xs">
            <Title order={4} c="white">
              Admin: {reportTemplate.name}
            </Title>
          </Paper>
          {reportTemplate.enabledForUser === false && (
            <Alert color="yellow" variant="light">
              <i className="fa fa-warning fa-fw" />
              Denne malen er foreløpig ikke tilgjengelig for brukere. Kontakt support når du ønsker å gjøre malen
              tilgjengelig.
            </Alert>
          )}
          {reportParseErrors != null && reportErrorsModalOpen === true && (
            <ZodErrorModal errors={reportParseErrors.issues} onAbort={() => setReportErrorsModalOpen(false)} />
          )}
          <Group p="md" justify="space-between" align="center" wrap="wrap">
            <Group wrap="wrap" gap="sm">
              {reportParseErrors != null && (
                <Button onClick={() => setReportErrorsModalOpen(true)} color="primary">
                  {reportParseErrors.issues?.length}
                  <i className="fa fa-warning fa-fw" />
                </Button>
              )}
              <Button color="mc-blue" onClick={downloadTemplate}>
                Laste ned mal
              </Button>
              <DocxUpload uploadCompleedCb={uploadComplete} uploadFunc={upload} />
              <a href="https://docxtemplater.com/demo/" target="_blank" rel="noreferrer">
                Se eksempler og dokumentasjon for rapportgenerator
                <i className="fa fa-external-link   fa-fw" />
              </a>
            </Group>
            <Button
              color="mc-blue"
              onClick={async () => {
                try {
                  await doGenerateReportAppDocx(
                    reportTemplate.templateId,
                    dataToGenerateDocxWith,
                    [],
                    0,
                    reportTemplate.templateId.toString(),
                  );
                } catch (error) {
                  const message =
                    typeof error?.message === "string"
                      ? error.message
                      : typeof error?.message?.message === "string"
                      ? error.message.message
                      : "Det er ingen docx-mal lastet opp";
                  dispatch(setShowFailedMessageThunk(message));
                }
              }}
            >
              <i className="fa fa-file-word-o fa-fw" />
              Generer dokument
            </Button>
          </Group>
          <hr />
          <Grid gutter="md" className="overflow-auto" style={{ flex: 1, minHeight: 0 }}>
            <Grid.Col span={{ base: 12, md: 6 }} className="d-flex h-100 overflow-auto">
              {reportTemplate.templateId === 101 && <EksempelRapport />}
              {reportTemplate.templateId === 102 && (
                <Brannrapport
                  onUpdateEditData={(data) => setData(data)}
                  data={reportTemplate.data as IBrandrapportConfig}
                />
              )}
              {reportTemplate.templateId === 103 && (
                <Geoteknikrapport
                  onUpdateEditData={(data) => setData(data)}
                  data={reportTemplate.data}
                  projectId={null}
                  userName=""
                />
              )}
              {reportTemplate.templateId === 104 && (
                <GKrapport
                  onUpdateEditData={(data) => setData(data)}
                  data={reportTemplate.data as IGKRapport}
                  createdAt={somedate}
                />
              )}
              {reportTemplate.templateId === 106 && (
                <GeoteknikOmraadestabilitetsRapport
                  onUpdateEditData={(data) => setData(data)}
                  data={reportTemplate.data}
                  projectId={null}
                  userName=""
                />
              )}
              {reportTemplate.templateId === 201 && (
                <RapportSamsvarserklaringFEL
                  samsvarserklaring={reportTemplate.data as IElektroSamsvarserklaringFELRapport}
                  onUpdateEditData={(data) => setData(data)}
                />
              )}
              {reportTemplate.templateId === 202 && (
                <RapportSamsvarserklaringEkomnett
                  data={reportTemplate.data as IElektroSamsvarserklaringEkomnettRapport}
                  onUpdateEditData={(data) => setData(data)}
                />
              )}
              {reportTemplate?.templateId === 205 && (
                <SJARapport data={reportTemplate.data as ISJArapport} onUpdateEditData={(data) => setData(data)} />
              )}
              {reportTemplate?.templateId === 301 && (
                <BygningsfysikkPremissrapport
                  data={reportTemplate.data as IBygningsfysikkPremissRapport}
                  onUpdateEditData={(data) => setData(data)}
                />
              )}
              {reportTemplate?.templateId === 401 && (
                <AkustikkPremissrapport onUpdateEditData={(data) => setData(data)} />
              )}
              {reportTemplate?.templateId === 701 && (
                <>
                  <UnderkonsulentRapport
                    data={reportTemplate.data as IInternJuridiskUnderkonsulentRapport}
                    onUpdateEditData={(data) => setData(data)}
                  />
                </>
              )}
              {reportTemplate?.templateId === 901 && (
                <StartupChecklistReport onUpdateEditData={(data) => setData(data)} />
              )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }} className="h-100" style={{ overflow: "auto" }}>
              <h4>Indata</h4>
              <div className="bg-dark d-flex flex-column" style={{ height: "500px", overflow: "auto" }}>
                <pre className="text-white">{JSON.stringify(editData, null, 2)}</pre>
              </div>
              <h4>Data som skickas för rapportgenerering:</h4>
              <div className="bg-dark d-flex flex-column" style={{ height: "500px", overflow: "auto" }}>
                <pre className="text-white">{JSON.stringify(dataToGenerateDocxWith, null, 2)}</pre>
              </div>
            </Grid.Col>
          </Grid>
        </>
      )}
      {reportTemplate == null && (
        <Alert color="yellow" variant="light">
          Ingen rapport med detta ID
        </Alert>
      )}
    </div>
  );
};

export default ReportAdminPage;
